/**
 * OpenInAppBanner - Shows "Open in App" prompt for mobile web users
 *
 * Only shown on mobile browsers (iOS Safari, Android Chrome), NOT on:
 * - Desktop browsers
 * - Native Capacitor app
 *
 * This helps mobile users open magic links in the native app for a better experience.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { isNative } from '@/lib/capacitor';
import './OpenInAppBanner.css';

interface OpenInAppBannerProps {
  /** Callback when user chooses to continue in browser */
  onContinueInBrowser?: () => void;
}

function isMobileWeb(): boolean {
  // CRITICAL: never the native app. The Capacitor WKWebView's user-agent still
  // contains "iPhone"/"iPad", so UA sniffing alone treats the native app as
  // mobile web — which renders the "Open in App" banner INSIDE the app (its
  // "Open App" fires xavior://auth/verify → reopens the app → infinite loop) and
  // makes useShouldPauseForBanner() pause MagicLinkVerify forever, so SSO/magic
  // link "open the app but nothing happens." Gate on the Capacitor runtime.
  if (isNative()) return false;
  const userAgent = navigator.userAgent;
  return /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
}

function buildAppLink(path: string, search: string): string {
  return `xavior:/${path}${search}`;
}

const BANNER_DISMISSED_KEY = 'xavior_app_banner_dismissed';
const CONTINUE_IN_BROWSER_EVENT = 'xavior:continue-in-browser';

function dispatchContinueInBrowserEvent(): void {
  window.dispatchEvent(new CustomEvent(CONTINUE_IN_BROWSER_EVENT));
}

export function onContinueInBrowser(callback: () => void): () => void {
  const handler = () => callback();
  window.addEventListener(CONTINUE_IN_BROWSER_EVENT, handler);
  return () => window.removeEventListener(CONTINUE_IN_BROWSER_EVENT, handler);
}

export function OpenInAppBanner({ onContinueInBrowser }: OpenInAppBannerProps) {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isMobileWeb()) {
      setShowBanner(false);
      return;
    }
    // Only show on the magic link verify page.
    if (!location.pathname.startsWith('/auth/verify')) {
      setShowBanner(false);
      return;
    }
    const wasDismissed = sessionStorage.getItem(BANNER_DISMISSED_KEY);
    if (wasDismissed) {
      setShowBanner(false);
      return;
    }
    setShowBanner(true);
  }, [location.pathname]);

  const handleOpenApp = useCallback(() => {
    const appLink = buildAppLink(location.pathname, location.search);
    console.log('[OpenInAppBanner] Opening app with URL:', appLink);

    window.location.href = appLink;
    // If the app isn't installed we stay here; the "Continue in Browser" option
    // remains available.
  }, [location.pathname, location.search]);

  const handleContinueInBrowser = useCallback(() => {
    console.log('[OpenInAppBanner] User chose to continue in browser');
    sessionStorage.setItem(BANNER_DISMISSED_KEY, 'true');
    setDismissed(true);
    setShowBanner(false);
    // Lets MagicLinkVerify start verification.
    dispatchContinueInBrowserEvent();
    onContinueInBrowser?.();
  }, [onContinueInBrowser]);

  if (!showBanner || dismissed) {
    return null;
  }

  return (
    <div className="open-in-app-banner">
      <div className="open-in-app-banner-content">
        <div className="open-in-app-banner-icon">📱</div>
        <div className="open-in-app-banner-text">
          <span className="open-in-app-banner-title">Continue in the XAVIOR app?</span>
          <span className="open-in-app-banner-subtitle">Better experience with the native app</span>
        </div>
      </div>
      <div className="open-in-app-banner-actions">
        <button
          className="open-in-app-banner-btn open-in-app-banner-btn-primary"
          onClick={handleOpenApp}
        >
          Open App
        </button>
        <button
          className="open-in-app-banner-btn open-in-app-banner-btn-secondary"
          onClick={handleContinueInBrowser}
        >
          Continue in Browser
        </button>
      </div>
    </div>
  );
}

/**
 * Whether to pause for the banner. Must be synchronous to prevent a race with
 * verification.
 */
function shouldPauseForBannerSync(pathname: string): boolean {
  if (!isMobileWeb()) {
    return false;
  }
  if (!pathname.startsWith('/auth/verify')) {
    return false;
  }
  const wasDismissed = sessionStorage.getItem(BANNER_DISMISSED_KEY);
  if (wasDismissed) {
    return false;
  }

  return true;
}

/**
 * Hook to check if the banner is being shown
 * Used by MagicLinkVerify to pause verification while banner is visible
 *
 * IMPORTANT: Uses synchronous initial state to prevent race condition
 * where verification starts before we detect mobile web
 */
export function useShouldPauseForBanner(): boolean {
  const location = useLocation();

  // Synchronous initial value to prevent a race condition.
  const [shouldPause, setShouldPause] = useState(() =>
    shouldPauseForBannerSync(location.pathname)
  );

  useEffect(() => {
    setShouldPause(shouldPauseForBannerSync(location.pathname));
  }, [location.pathname]);

  return shouldPause;
}

export function dismissAppBanner(): void {
  sessionStorage.setItem(BANNER_DISMISSED_KEY, 'true');
}
